import React from "react";
import { formatDateTime } from "../../../utils/formatDate";

export interface Conversation {
	id: string;
	buyer_name: string;
	propertyInterest: string;
	lastMessage: string;
	lastMessageTime: Date;
	unreadCount: number;
	status?: "online" | "away" | "offline";
	avatar?: string | null;
}

interface ConversationListProps {
	conversations: Conversation[];
	selectedConversation: Conversation | null;
	onConversationSelect: (conversation: Conversation) => void;
	onStartNewConversation: () => void;
	searchQuery: string;
	onSearchChange: (query: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
	conversations,
	selectedConversation,
	onConversationSelect,
	onStartNewConversation,
	searchQuery,
	onSearchChange,
}) => {
	// Using centralized formatDateTime utility

	const getStatusColor = (status?: string) => {
		switch (status) {
			case "online":
				return "bg-green-500";
			case "away":
				return "bg-yellow-500";
			case "offline":
				return "bg-gray-400";
			default:
				return "bg-gray-400";
		}
	};

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="p-4 border-b border-border">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold text-foreground">Messages</h2>
					<button
						onClick={onStartNewConversation}
						className="bg-primary text-primary-foreground p-2 rounded-lg hover:bg-primary/90 transition-colors"
						title="Start new conversation"
					>
						<svg
							className="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 4v16m8-8H4"
							/>
						</svg>
					</button>
				</div>

				{/* Search */}
				<div className="relative">
					<svg
						className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
						/>
					</svg>
					<input
						type="text"
						placeholder="Search conversations..."
						value={searchQuery}
						onChange={(e) => onSearchChange(e?.target?.value)}
						className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
					/>
				</div>
			</div>
			{/* Conversation List */}
			<div className="flex-1 overflow-y-auto">
				{conversations?.length === 0 ? (
					<div className="p-6 text-center">
						<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
							<svg
								className="w-6 h-6 text-muted-foreground"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.456-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z"
								/>
							</svg>
						</div>
						<p className="text-muted-foreground text-sm">
							No conversations yet
						</p>
						<p className="text-muted-foreground text-xs mt-1">
							Start a new conversation to begin
						</p>
					</div>
				) : (
					<div className="space-y-1 p-2">
						{conversations?.map((conversation: Conversation) => (
							<div
								key={conversation?.id}
								onClick={() => onConversationSelect(conversation)}
								className={`p-3 rounded-lg cursor-pointer transition-colors ${
									selectedConversation?.id === conversation?.id
										? "bg-primary text-primary-foreground"
										: "hover:bg-muted"
								}`}
							>
								<div className="flex items-start space-x-3">
									{/* Avatar */}
									<div className="relative">
										<div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
											{conversation?.avatar ? (
												<img
													src={conversation?.avatar}
													alt={`${conversation?.buyer_name} avatar`}
													className="w-10 h-10 rounded-full object-cover"
												/>
											) : (
												<span className="text-sm font-medium text-muted-foreground">
													{conversation?.buyer_name?.charAt(0)?.toUpperCase()}
												</span>
											)}
										</div>
										{conversation?.status && (
											<div
												className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${getStatusColor(
													conversation?.status,
												)}`}
											/>
										)}
									</div>

									{/* Content */}
									<div className="flex-1 min-w-0">
										<div className="flex items-center justify-between mb-1">
											<h4
												className={`text-sm font-medium truncate ${
													selectedConversation?.id === conversation?.id
														? "text-primary-foreground"
														: "text-foreground"
												}`}
											>
												{conversation?.buyer_name}
											</h4>
											<span
												className={`text-xs ${
													selectedConversation?.id === conversation?.id
														? "text-primary-foreground/70"
														: "text-muted-foreground"
												}`}
											>
												{formatDateTime(conversation?.lastMessageTime)}
											</span>
										</div>

										<p
											className={`text-xs mb-1 ${
												selectedConversation?.id === conversation?.id
													? "text-primary-foreground/80"
													: "text-muted-foreground"
											}`}
										>
											{conversation?.propertyInterest}
										</p>

										<div className="flex items-center justify-between">
											<p
												className={`text-xs truncate ${
													selectedConversation?.id === conversation?.id
														? "text-primary-foreground/70"
														: "text-muted-foreground"
												}`}
											>
												{conversation?.lastMessage}
											</p>
											{conversation?.unreadCount > 0 && (
												<span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 ml-2">
													{conversation?.unreadCount}
												</span>
											)}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
			{/* Quick Stats */}
			<div className="p-4 border-t border-border bg-muted/20">
				<div className="grid grid-cols-2 gap-4 text-center">
					<div>
						<div className="text-lg font-semibold text-foreground">
							{conversations?.length}
						</div>
						<div className="text-xs text-muted-foreground">Total Chats</div>
					</div>
					<div>
						<div className="text-lg font-semibold text-foreground">
							{
								conversations?.filter((c: Conversation) => c?.unreadCount > 0)
									?.length
							}
						</div>
						<div className="text-xs text-muted-foreground">Unread</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ConversationList;
